import { useNavigate } from "@tanstack/react-router";
import { useCallback, type MouseEvent, type ReactNode } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api/client";
import { useT } from "@/lib/i18n";
import { usePeek } from "@/lib/peek";
import { cn } from "@/lib/utils";

/** The full person page (Customer 360). */
export const personPath = (contactId: string) => `/people/${contactId}`;

/** Resolve an @username to a contact id (exact handle match), for places that only know the username. */
export async function resolveContactId(username: string): Promise<string | null> {
  const handle = username.replace(/^[\s‎@]+/, "").toLowerCase();
  if (!handle) return null;
  const r = await api("contactSearch", { query: { q: handle, limit: 5 } });
  return r.items.find((h) => h.handle?.toLowerCase() === handle)?.contactId ?? null;
}

/** Open a person: plain click → person quick panel (`?peek=contact:<id>`); Ctrl/⌘-click → the full profile page. */
export function useOpenPerson() {
  const { open } = usePeek();
  const navigate = useNavigate();
  return useCallback(
    (contactId: string, full = false) => {
      if (full) void navigate({ to: "/people/$contactId", params: { contactId } });
      else open("contact", contactId);
    },
    [navigate, open],
  );
}

/** Does this click ask for the full page (Ctrl/⌘-click)? Middle clicks are left to the browser (new tab). */
export const wantsFullPage = (e: Pick<MouseEvent, "metaKey" | "ctrlKey">) => e.metaKey || e.ctrlKey;

/**
 * Every username / avatar in the app goes through this: a real link to the person page (so middle click and "copy
 * link" work) whose plain click opens the person quick panel and whose Ctrl/⌘-click opens the full profile. When only
 * the username is known (`contactId` null), the contact is resolved by exact handle on click.
 */
export function PersonLink({
  contactId,
  username,
  children,
  className,
  testId = "person-link",
  decorative,
}: {
  contactId: string | null | undefined;
  username?: string | null;
  children: ReactNode;
  className?: string;
  testId?: string;
  /** A second link to the same person (e.g. the avatar next to the name): out of the tab order and hidden from AT. */
  decorative?: boolean;
}) {
  const { t } = useT();
  const openPerson = useOpenPerson();
  const onClick = async (e: MouseEvent) => {
    if (e.button !== 0 || e.shiftKey || e.altKey) return;
    e.preventDefault();
    e.stopPropagation();
    const full = wantsFullPage(e);
    let id = contactId ?? null;
    if (!id && username) id = await resolveContactId(username).catch(() => null);
    if (id) openPerson(id, full);
    else toast.message(t("people360.notFound"));
  };
  const cls = cn("rounded-sm text-start underline-offset-2 outline-none hover:underline focus-visible:shadow-[var(--ring)]", className);
  if (!contactId && !username) return <>{children}</>;
  return (
    <a href={contactId ? personPath(contactId) : "#"} onClick={(e) => void onClick(e)} className={cls} data-testid={testId} data-contact-id={contactId ?? undefined} data-username={username ?? undefined} {...(decorative ? { tabIndex: -1, "aria-hidden": true } : {})}>
      {children}
    </a>
  );
}
